/*
* Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
*/
import React, {ReactNode} from 'react';
import {makeStyles} from '@material-ui/core';
import useMedia from '../../hooks/useMedia';
import Render from '../../render/Render';
import {LabelTypography} from '../../typography/LabelTypography';

// defines the style def for LabelIconButton component...
const useStyles = makeStyles(() => ({
  root : {
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center'
  },
  label : {
    fontSize : '14px'
  }
}));

// defines the props def for LabelIconButton component...
export interface LabelIconButtonProps {
  label?: string;
  children: ReactNode;
  displayLabel?: boolean;
};

/**
 * A flexible button-like wrapper that combines an icon (or any child element)
 * with an optional text label.  
 * 
 * This component automatically hides the label on smaller screens (like phones)
 * using the `useMedia` hook, and can be configured to always or conditionally
 * display the label.
 *
 * ```typescript
 * import {LabelIconButton} from './LabelIconButton';
 * import {IconButton} from '@material-ui/core';
 * import {Edit} from '@material-ui/icons';
 *
 * export default function Example() {
 *   return (
 *     <LabelIconButton label="Edit">
 *       <IconButton>
 *         <Edit />
 *       </IconButton>
 *     </LabelIconButton>
 *   );
 * }
 * ```
 *
 * @param props.children - The icon or custom content to render inside the button.
 * @param [props.label] - The optional text label displayed next to the icon.
 * @param [props.displayLabel=true] - Whether to display the label (ignored on small screens).
 *
 * @returns {JSX.Element} A combined icon and label button element.
 *
 */
export const LabelIconButton = ({
  label,
  children,
  displayLabel = true
}: any) => {
  const classes   = useStyles();
  const {isPhone} = useMedia();
  const showLabel = !isPhone && displayLabel && !!label;
  return (
    <div className={classes.root}>
      {children}
      <Render.If condition={showLabel}>
        <LabelTypography variantType='label2' className={classes.label}>
          {label}
        </LabelTypography>
      </Render.If>
    </div>
  );
};

export default LabelIconButton;
